import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePremium } from '../contexts/PremiumContext';
import { useTranslation } from '../hooks/useTranslation';
import PremiumUpgradeModal from './PremiumUpgradeModal';
import { createCustomerPortalSession, syncSubscriptionStatus } from '../firebase';

const SubscriptionManagement = ({ onBack }) => {
  const { currentUser } = useAuth();
  const { isPremium, loading: premiumLoading } = usePremium();
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const handleUpgradeClick = () => {
    setShowUpgradeModal(true);
  };

  const handleCloseUpgradeModal = () => {
    setShowUpgradeModal(false);
  };

  const handleManageSubscription = async () => {
    setIsProcessing(true);
    setMessage(null);

    try {
      console.log('🔄 Creating customer portal session...');
      const returnUrl = `${window.location.origin}${window.location.pathname}`;
      const result = await createCustomerPortalSession(returnUrl);
      console.log('✅ Portal session result:', result);
      
      if (result && result.url) {
        console.log('🚀 Opening portal in new window:', result.url);
        // Open Stripe Customer Portal in a new window
        window.open(result.url, '_blank', 'noopener,noreferrer');
        setIsProcessing(false);
      } else {
        throw new Error('No URL returned from portal session');
      }
    } catch (error) {
      console.error('❌ Error opening customer portal:', error);
      let errorMessage = t('unableToOpenPortal');
      
      if (error.message.includes('No configuration')) {
        errorMessage += t('portalNotConfigured');
      } else if (error.message.includes('No Stripe customer ID')) {
        errorMessage += t('noSubscriptionFound');
      } else {
        errorMessage += ` Error: ${error.message}`;
      }
      
      setMessage({
        type: 'error',
        text: errorMessage
      });
      setIsProcessing(false);
    }
  };

  const handleSyncStatus = async () => {
    setIsSyncing(true);
    setMessage(null);

    try {
      console.log('🔄 Syncing subscription status...');
      const result = await syncSubscriptionStatus();
      console.log('✅ Sync result:', result);
      
      if (result?.isPremium) {
        setMessage({
          type: 'success',
          text: t('subscriptionStatusUpdated')
        });
        // Force a refresh of premium status
        window.location.reload();
      } else {
        setMessage({
          type: 'info',
          text: result?.message || t('noActiveSubscription')
        });
      }
    } catch (error) {
      console.error('❌ Error syncing subscription status:', error);
      setMessage({
        type: 'error',
        text: t('failedToSyncStatus', { message: error.message })
      });
    } finally {
      setIsSyncing(false);
    }
  };

  if (premiumLoading) {
    return (
      <div className="subscription-management">
        <div className="subscription-header">
          <button className="back-button" onClick={onBack}>
            ← {t('backToHome')}
          </button>
          <h1>{t('subscriptionManagement')}</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('loadingSubscriptionInfo')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-management">
      <div className="subscription-header">
        <button className="back-button" onClick={onBack}>
          ← {t('backToHome')}
        </button>
        <h1>{t('subscriptionManagement')}</h1>
      </div>

      <div className="subscription-content">
        <div className="subscription-status">
          <h2>{t('currentStatus')}</h2>
          <div className={`status-badge ${isPremium ? 'premium' : 'free'}`}>
            {isPremium ? `⭐ ${t('premium')}` : `🆓 ${t('free')}`}
          </div>
        </div>

        {message && (
          <div className={`message message-${message.type}`}>
            <span>{message.text}</span>
            <button 
              className="message-close"
              onClick={() => setMessage(null)}
            >
              ×
            </button>
          </div>
        )}

        {isPremium ? (
          <div className="premium-management">
            <h3>{t('premiumFeatures')}</h3>
            <div className="features-list">
              <div className="feature-item">
                <span className="feature-icon">🛤️</span>
                <div>
                  <strong>{t('deepeningRelationshipsJourney')}</strong>
                  <p>{t('journeyDescriptionShort')}</p>
                </div>
              </div>
              <div className="feature-item">
                <span className="feature-icon">👫</span>
                <div>
                  <strong>{t('friendshipTrackerFeature')}</strong>
                  <p>{t('friendshipTrackerDescription')}</p>
                </div>
              </div>
            </div>

            <div className="subscription-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleManageSubscription}
                disabled={isProcessing}
              >
                {isProcessing ? t('processing') : t('manageSubscription')}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleSyncStatus}
                disabled={isSyncing}
                title={t('syncSubscriptionStatus')}
              >
                {isSyncing ? t('syncing') : t('syncStatus')}
              </button>
            </div>
          </div>
        ) : (
          <div className="free-management">
            <h3>{t('upgradeToPremium')}</h3>
            <p>{t('unlockPremiumFeatures')}</p>
            
            <div className="current-access-info">
              <h4>{t('yourCurrentAccess')}</h4>
              <ul>
                <li>{t('accessTo1Friend')}</li>
                <li>{t('journeysPaused')}</li>
                <li>{t('cannotAddMoreFriends')}</li>
              </ul>
              <p><em>{t('upgradeToRestoreAccess')}</em></p>
            </div>
            
            <div className="features-list">
              <div className="feature-item">
                <span className="feature-icon">🛤️</span>
                <div>
                  <strong>{t('joinOnlineJourneys')}</strong>
                  <p>{t('journeyDescription')}</p>
                </div>
              </div>
              <div className="feature-item">
                <span className="feature-icon">👫</span>
                <div>
                  <strong>{t('friendshipTrackerFeature')}</strong>
                  <p>{t('friendshipTrackerDescription')}</p>
                </div>
              </div>
            </div>

            <div className="upgrade-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleUpgradeClick}
              >
                {t('upgradeToPremium')}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleSyncStatus}
                disabled={isSyncing}
                title={t('checkSubscriptionStatus')}
              >
                {isSyncing ? t('syncing') : t('checkSubscriptionStatusButton')}
              </button>
            </div>
          </div>
        )}

        <div className="account-info">
          <h3>{t('accountInformation')}</h3>
          <div className="account-details">
            <p><strong>{t('emailLabel')}</strong> {currentUser?.email}</p>
            <p><strong>{t('userIdLabel')}</strong> {currentUser?.uid}</p>
            <p><strong>{t('memberSince')}</strong> {currentUser?.metadata?.creationTime ? new Date(currentUser.metadata.creationTime).toLocaleDateString() : 'N/A'}</p>
          </div>
        </div>

        <div className="support-info">
          <h3>{t('needHelp')}</h3>
          <p>{t('supportMessage')}</p>
          <p className="support-email">
            <strong>{t('emailLabel')}</strong> <a href="mailto:hello@upsanddowns.life">hello@upsanddowns.life</a>
          </p>
        </div>
      </div>

      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal 
        isOpen={showUpgradeModal}
        onClose={handleCloseUpgradeModal}
        reason="subscription_management"
      />
    </div>
  );
};

export default SubscriptionManagement;
